import React from "react";
import { Box, Typography, Card, Button } from "@mui/material";
import { BsShieldCheck, BsInfoCircleFill } from "react-icons/bs";

export const labelStyle = {
    fontSize: '11px',
    fontWeight: 700,
    color: '#475569',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '6px',
    display: 'block'
};

export const FormHeroHeader = ({ icon, title, subtitle, badgeText = "Verified Record" }) => (
    <Box sx={{
        mb: 4, p: { xs: 2.5, md: 3.5 },
        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
        borderRadius: '16px',
        border: '1px solid #e2e8f0',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.02), 0 2px 4px -1px rgba(0, 0, 0, 0.01)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: 2
    }}>
        <Box display="flex" alignItems="center" gap={2}>
            <Box sx={{
                width: 52, height: 52, borderRadius: '14px',
                background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                color: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(37, 99, 235, 0.25)',
                flexShrink: 0
            }}>
                {icon}
            </Box>
            <Box>
                <Typography variant="h5" sx={{ fontWeight: 800, color: '#0f172a', letterSpacing: '-0.5px', mb: 0.5, fontSize: { xs: '1.25rem', md: '1.5rem' } }}>
                    {title || "Form Setup"}
                </Typography>
                {subtitle && (
                    <Typography variant="body2" sx={{ color: '#64748b', fontSize: '13px' }}>
                        {subtitle}
                    </Typography>
                )}
            </Box>
        </Box>
        {badgeText && (
            <Box display="flex" alignItems="center" gap={1} sx={{ bgcolor: '#eff6ff', px: 2, py: 0.8, borderRadius: '20px', border: '1px solid #bfdbfe' }}>
                <BsShieldCheck color="#2563eb" size={16} />
                <Typography fontSize={12} fontWeight={700} color="#1e40af">{badgeText}</Typography>
            </Box>
        )}
    </Box>
);

export const FormSectionHeader = ({ icon, title, subtitle }) => (
    <Box display="flex" alignItems="center" gap={1.5} mb={2.5} pb={1.5} borderBottom="1px solid #f1f5f9">
        <Box sx={{
            width: 38, height: 38, borderRadius: '10px',
            background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
            color: '#2563eb', display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 2px 4px rgba(37, 99, 235, 0.08)',
            flexShrink: 0
        }}>
            {icon}
        </Box>
        <Box>
            <Typography fontSize={15} fontWeight={700} color="#0f172a" lineHeight={1.2}>
                {title}
            </Typography>
            {subtitle && (
                <Typography fontSize={12} color="#64748b" mt={0.3}>
                    {subtitle}
                </Typography>
            )}
        </Box>
    </Box>
);

export const FormSectionCard = ({ icon, title, subtitle, children, sx = {} }) => (
    <Card sx={{ p: { xs: 2.5, md: 3.5 }, mb: 4, borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 4px 12px -2px rgba(0, 0, 0, 0.03)', bgcolor: '#ffffff', overflow: 'visible', ...sx }}>
        {(icon || title) && <FormSectionHeader icon={icon} title={title} subtitle={subtitle} />}
        {children}
    </Card>
);

export const FormFooter = ({ 
    onCancel, 
    onSubmitClick,
    backText = "Back", 
    submitText = "Save", 
    isEdit = false,
    backTestId = "back-btn", 
    saveTestId = "save-btn",
    note = "Ensure all required fields marked with an asterisk (*) are accurate before saving." 
}) => {
    let buttonLabel = submitText;
    if (isEdit) {
        if (!buttonLabel || buttonLabel === "Save") {
            buttonLabel = "Update";
        } else if (buttonLabel.toLowerCase().startsWith("save ")) {
            buttonLabel = buttonLabel.replace(/^save\s+/i, "Update ");
        } else if (buttonLabel.toLowerCase().startsWith("apply ")) {
            buttonLabel = buttonLabel.replace(/^apply\s+/i, "Update ");
        } else if (!buttonLabel.toLowerCase().includes("update")) {
            buttonLabel = `Update ${buttonLabel}`;
        }
    }

    return (
        <Box sx={{
            mt: 4, p: { xs: 2, md: 2.5 },
            background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
            borderRadius: '14px',
            border: '1px solid #e2e8f0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2
        }}>
            <Box display="flex" alignItems="center" gap={1.5}>
                <BsInfoCircleFill color="#64748b" size={18} />
                <Typography fontSize={13} color="#475569" fontWeight={500}>
                    {note}
                </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1.5} sx={{ width: { xs: '100%', sm: 'auto' }, justifyContent: { xs: 'flex-end', sm: 'flex-start' } }}>
                {onCancel && (
                    <Button
                        data-testid={backTestId}
                        variant="outlined"
                        onClick={onCancel}
                        type="button"
                        sx={{
                            borderColor: '#cbd5e1',
                            color: '#475569',
                            bgcolor: '#ffffff',
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '13px',
                            borderRadius: '8px',
                            px: 3, py: 1,
                            transition: 'all 0.2s',
                            '&:hover': { bgcolor: '#f8fafc', borderColor: '#94a3b8', transform: 'translateY(-1px)' }
                        }}
                    >
                        {backText}
                    </Button>
                )}
                <Button
                    data-testid={saveTestId}
                    type="submit"
                    onClick={onSubmitClick}
                    variant="contained"
                    sx={{
                        background: 'linear-gradient(135deg, #2563eb, #1d4ed8)',
                        color: '#ffffff',
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: '13px',
                        borderRadius: '8px',
                        px: 4, py: 1,
                        boxShadow: '0 2px 4px rgba(37, 99, 235, 0.25)',
                        transition: 'all 0.2s',
                        '&:hover': {
                            background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                            boxShadow: '0 4px 12px rgba(37, 99, 235, 0.35)',
                            transform: 'translateY(-1px)'
                        }
                    }}
                >
                    {buttonLabel}
                </Button>
            </Box>
        </Box>
    );
};
